'use client';

import { MapPin, Navigation } from 'lucide-react';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { DirectionalArrow } from '~/(tenant)/_components/native/_primitives';

export interface LocationData {
  id: string;
  name: string;
  slug: string;
  city: string | null;
  googleMapsUrl: string | null;
}

interface LocationSectionProps {
  locations: LocationData[];
}

export function LocationSection({ locations }: LocationSectionProps) {
  if (locations.length === 0) return null;

  return (
    <section style={{ padding: '24px 22px 0' }}>
      {locations.map((location, i) => (
        <LocationRow key={location.id} location={location} isFirst={i === 0} />
      ))}
    </section>
  );
}

function LocationRow({ location, isFirst }: { location: LocationData; isFirst: boolean }) {
  const t = useNativeTokens();
  const hasMap = !!location.googleMapsUrl;

  const content = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px 0',
        borderTop: isFirst ? `1px solid ${t.line}` : 'none',
        borderBottom: `1px solid ${t.line}`,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: t.foreground,
          opacity: 0.7,
          flexShrink: 0,
        }}
      >
        <MapPin size={18} strokeWidth={1.3} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 17,
            color: t.foreground,
            fontWeight: 400,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {location.name}
        </div>
        {location.city && (
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: t.muted,
              marginTop: 4,
            }}
          >
            {location.city}
          </div>
        )}
      </div>

      <div style={{ color: t.foreground, opacity: 0.6, flexShrink: 0 }}>
        {hasMap ? <Navigation size={14} strokeWidth={1.4} /> : <DirectionalArrow size={14} strokeWidth={1.4} />}
      </div>
    </div>
  );

  if (hasMap) {
    return (
      <a
        href={location.googleMapsUrl!}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'block', textDecoration: 'none' }}
      >
        {content}
      </a>
    );
  }

  return content;
}
